import jwt_decode from "jwt-decode"
import * as api from "../clients/slashid-api"
import * as domain from "../domain/index"
import { SlashIDOptions, createSlashIDConfig } from "../domain/options"
import { versionInfo as version } from "../version"
import { Bucket, BucketName, DefaultBucketName } from "./bucket"
import { Configuration, DeleteConsentGdprRequest, DefaultConfig, TokensApi } from "../clients/slashid-api"
import { slashIDOptionsToCreateAnonymousUserOptions, createAnonymousUserToken } from "./user.utils"
import * as internalErrors from "../domain/errors.internal"
import * as errors from "../domain/errors"

export type UserToken = api.UserToken & Partial<domain.JsonObject>

export type Token = UserToken | api.TokenContainer

export type GDPRConsentLevels = {
    consentLevels: api.GDPRConsentLevel[]
}

function isTokenContainer(token: Token): token is api.TokenContainer {
    if (!token) {
        return false
    }

    if (typeof (token as api.TokenContainer).user_token === "string") {
        return true
    }

    return false
}

/**
 * Objects of this type represent an authenticated user. If you have a token you can instantiate your own User.
 * This class is server side rendering (SSR) friendly as it only uses the fetch API with no other dependencies on browser APIs.
 *
 * A User object gives you access to methods to:
 *
 *  - access or associate arbitrary private data to the user: please see the {@link getBucket} method;
 *    your backend will also be able to retrieve the data associated with your user by invoking the related SlashID User Management API {@link https://developer.slashid.dev/docs/api/get-persons-person-id-attributes-bucket-name/};
 *  - contextual user attributes as detailed in the _Accessors_ section below;
 *
 * @remarks User objects are safe for serialization, you can pass them to other contexts with the `postMessage` family of functions.
 *
 */
export class BaseUser {
    protected _apiClient!: api.DefaultApi
    protected _token: string | undefined
    private _attrsClient!: api.AttributesApi
    private _tokenClient!: TokensApi
    private _personConsentsApi!: api.PersonConsentsApi
    private _tokenContainer?: string
    private _clientOptions?: SlashIDOptions

    static async createAnonymousUser(options: SlashIDOptions): Promise<BaseUser> {
        const createAnonUserOptions = slashIDOptionsToCreateAnonymousUserOptions(options)
        const token = await createAnonymousUserToken(createAnonUserOptions)

        return new BaseUser(token, options)
    }

    /**
     * Reconstruct an authenticated user from its token value.
     *
     * @param token A user {@link token} value.
     * @param optionsOrSid SlashID instance or SlashID connection options. It is preferred to pass in the SlashID instance
     *
     * @throws `TypeError` If the given `optionsOrSid`, if defined, fail validation, or
     * the given token cannot be decoded.
     */
    constructor(token: domain.UserTokenText | domain.TokenContainerText, options?: SlashIDOptions) {
        this.initialize(token, options)
    }

    /**
     * Must be called in the constructor, before anything else happens.
     * Initializes the internal state of the instance.
     */
    private initialize(token: domain.UserTokenText | domain.TokenContainerText, options?: SlashIDOptions) {
        try {
            // try JSON-decode it, just in case this is somehow coming back from a
            // previous JSON.stringify(user) without having been JSON-parsed first
            token = JSON.parse(token)
        } catch (_) {
            // ignored
        }

        if (typeof token !== "string") {
            throw new internalErrors.SlashIDError({
                name: errors.ERROR_NAMES.invalidActionError,
                message: "token must be a string",
                context: { token },
            })
        }

        try {
            const decodedToken = jwt_decode<UserToken | api.TokenContainer>(token)
            if (isTokenContainer(decodedToken)) {
                this._token = decodedToken.user_token
                this._tokenContainer = token
            } else {
                this._token = token
            }
        } catch (e: any) {
            throw new internalErrors.SlashIDError({
                name: errors.ERROR_NAMES.unknown,
                message: `error when decoding token: ${e.toString()}`,
                cause: e,
                context: { token },
            })
        }

        // TODO get the environment config from the token
        this._clientOptions = createSlashIDConfig(options ?? {})
        this.initializeClients()
    }

    /**
     * Use a new token to update the internal state of the user. Same set of options is reused.
     */
    protected updateToken(token: domain.UserTokenText) {
        this.initialize(token, this._clientOptions)
    }

    protected get decoded(): UserToken {
        if (!this._token) {
            throw new internalErrors.SlashIDError({
                name: errors.ERROR_NAMES.invalidActionError,
                message: "Cannot call methods on unauthenticated user",
            })
        }
        try {
            return jwt_decode(this._token)
        } catch (e: any) {
            throw new internalErrors.SlashIDError({
                name: errors.ERROR_NAMES.unknown,
                message: `error when decoding token: ${e.toString()}`,
                cause: e,
                context: { token: this._token },
            })
        }
    }

    /**
     * The claims of the user token.
     * @returns {UserToken}
     */
    public get tokenClaims(): UserToken {
        return this.decoded
    }

    protected get decodedTokenContainer(): api.TokenContainer | undefined {
        if (!this._tokenContainer) {
            return
        }
        try {
            return jwt_decode<api.TokenContainer | undefined>(this._tokenContainer)
        } catch (e: any) {
            throw new internalErrors.SlashIDError({
                name: errors.ERROR_NAMES.unknown,
                message: `error when decoding token container: ${e.toString()}`,
                cause: e,
                context: { tokenContainer: this._tokenContainer },
            })
        }
    }

    /**
     * If the user instance is created with a {@link Types.TokenContainer} this will return the claims of the token container.
     * Otherwise it will return undefined.
     * @returns {api.TokenContainer | undefined}
     */
    public get tokenContainerClaims(): api.TokenContainer | undefined {
        return this.decodedTokenContainer
    }

    protected updateClient(token?: domain.UserTokenText) {
        this._token = token
        this.initializeClients()
    }

    /**
     * Initializes every client with new Configuration based on currently stored
     * _clientOptions and _token.
     */
    protected initializeClients() {
        const clientConfig = new Configuration({
            ...DefaultConfig,
            ...(this._clientOptions?.baseURL && { basePath: this._clientOptions.baseURL }),
            accessToken: this._token,
        })
        this._apiClient = new api.DefaultApi(clientConfig)
        this._attrsClient = new api.AttributesApi(clientConfig)
        this._tokenClient = new TokensApi(clientConfig)
        this._personConsentsApi = new api.PersonConsentsApi(clientConfig)
    }

    /**
     * This user's ID. Use this property in your backend services when interacting with the [SlashID User Management API](https://developer.slashid.dev/docs/category/api/persons).
     */
    public get ID(): string {
        return this.decoded.person_id!!
    }

    /**
     * The entire, signed authentication token of this user.
     */
    public get token(): string {
        return this._token ?? ""
    }

    /**
     * The entire token container
     * @returns either the token container string or an empty string
     */
    public get tokenContainer(): string {
        return this._tokenContainer || ""
    }

    /**
     * The organization ID this user belongs to.
     */
    public get oid(): string {
        return this.decoded.oid!!
    }

    /**
     * Indicates whether the user has been just registered, otherwise it's a returning user.
     */
    public get firstLogin(): boolean {
        return this.decoded.first_token!!
    }

    /**
     * Indicates which authentication methods the user has been verified with. It can contain multiple items in case of multi factor authentication.
     */
    public get authentication(): api.FactorMethod[] {
        return this.decoded.authenticated_methods || []
    }

    /**
     * Indicates which authentication methods the user has been verified with, including the handles used for each method.
     */
    public get authentications(): api.Authentication[] {
        return this.decoded.authentications || []
    }

    /**
     * Indicates if the user is an anonymous user
     */
    public get anonymous(): boolean {
        return this.decoded.person_type === "anonymous"
    }

    /**
     * Indicates if the user is authenticated
     *
     * For anonymous users this will return `false`
     */
    public async isAuthenticated(): Promise<boolean> {
        const { valid } = await this.validateToken()
        return valid && this.decoded.person_type !== "anonymous"
    }

    /**
     * Create a DirectID based on your token
     * @returns directID token
     */
    public async createDirectID() {
        const { result: directID } = await this._apiClient.postDirectId({
            slashIDSdkVersion: version.raw,
        })

        return directID
    }

    /**
     * Log out of the current session. Clears the SlashID token.
     * Will attempt revoking the token server-side.
     */
    public async logout() {
        try {
            await this._tokenClient.postTokenRevoke({
                slashIDSdkVersion: version.raw,
                revokeTokenReq: { token: this.token },
            })
        } catch (_) {
            // we ignore the error as revoking the token is a side effect
            // this endpoint will return a 400 any time you try to revoke a token that is already revoked or expired
            // we expect this to happen frequently
        }

        this.updateClient(undefined)
        this._tokenContainer = undefined
    }

    /**
     * Resolves to a token validity info object which tells if the token is genuine and if it has expired yet.
     * @returns {Promise} Token validity info
     */
    public async validateToken(): Promise<api.ValidateTokenResponse> {
        const response = await this._tokenClient?.postTokenValidate(
            { validateTokenReq: { token: this.token }, slashIDSdkVersion: version.raw },
            {
                cache: "no-cache",
            }
        )

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return response.result!!
    }

    /**
     * Get an array of group names that the user belongs to.
     */
    public getGroups(): string[] {
        const groupsClaimName = this.decoded.groups_claim_name || "groups"

        const token = this.decoded
        return token[groupsClaimName] as string[]
    }

    /**
     * Exposes the attributes client so the Bucket instance can access it
     */
    public getAttributesClient() {
        return this._attrsClient
    }

    /**
     *  Creates a {@link Types.Bucket} object used to access attributes.
     *
     * @param bucketName name of the bucket we want to access - uses "end_user_read_write" as default.
     * You can pass in any string that corresponds to a name of a bucket set up for your organization.
     * You can use any of the preset bucket names with corresponding permissions and scopes - {@link Types.DefaultBucketName}.
     * @returns
     */
    public getBucket(bucketName: BucketName = DefaultBucketName.end_user_read_write): Bucket {
        const bucket = new Bucket({ user: this, bucketName: bucketName })
        return bucket
    }

    /**
     * @deprecated Use {@link getBucket} instead.
     */
    public async get<AttributesType extends domain.JsonObject>(attributeNames?: string[]): Promise<AttributesType> {
        const endUserReadWriteBucket = this.getBucket()
        return endUserReadWriteBucket.get(attributeNames)
    }

    /**
     * @deprecated Use {@link getBucket} instead.
     */
    public async set<AttributesType extends domain.JsonObject>(attributes: AttributesType): Promise<void> {
        const endUserReadWriteBucket = this.getBucket()
        return endUserReadWriteBucket.set(attributes)
    }

    /**
     * @deprecated Use {@link getBucket} instead.
     */
    public async delete(attributeNames: string[]): Promise<any> {
        const endUserReadWriteBucket = this.getBucket()
        return endUserReadWriteBucket.delete(attributeNames)
    }

    /**
     * Fetch the GDPR consent levels for the current user.
     * @returns {Promise} GDPR consent info
     */
    public async getGDPRConsent(): Promise<api.GDPRConsentResponse> {
        const response = await this._personConsentsApi.getConsentGdpr({
            slashIDSdkVersion: version.raw,
        })

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return response.result!!
    }

    /**
     * Set the GDPR consent levels for the current user.
     * This will overwrite any existing consent levels and set the consent levels to only the ones included with the request.
     *
     * @param {api.GDPRConsentRequest} request with consentLevels to set
     * @returns {Promise} GDPR consent info
     */
    public async setGDPRConsent({ consentLevels }: GDPRConsentLevels): Promise<api.GDPRConsentResponse> {
        const response = await this._personConsentsApi.putConsentGdpr({
            gDPRConsentRequest: { consent_levels: consentLevels },
            slashIDSdkVersion: version.raw,
        })

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return response.result!!
    }

    /**
     * Add the GDPR consent levels to the current user.
     * Consent levels not included in the request will not be changed.
     *
     * @param {api.GDPRConsentRequest} request with consent levels to add
     * @returns {Promise} GDPR consent info
     */
    public async addGDPRConsent({ consentLevels }: GDPRConsentLevels): Promise<api.GDPRConsentResponse> {
        const response = await this._personConsentsApi.postConsentGdpr({
            gDPRConsentRequest: { consent_levels: consentLevels },
            slashIDSdkVersion: version.raw,
        })

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return response.result!!
    }

    /**
     * Remove the GDPR consent levels from the current user.
     * Consent levels not included in the request will not be changed.
     *
     * @param {api.ConsentGdprDeleteRequest} request with consentLevels to remove
     */
    public async removeGDPRConsent({ consentLevels }: DeleteConsentGdprRequest): Promise<void> {
        return this._personConsentsApi.deleteConsentGdpr({
            slashIDSdkVersion: version.raw,
            consentLevels,
        })
    }

    /**
     * Remove all stored GDPR consent levels from the current user.
     * Consent levels not included in the request will not be changed, unless deleteAll flag is set to true.
     */
    public async removeGDPRConsentAll(): Promise<void> {
        return this._personConsentsApi.deleteConsentGdpr({
            slashIDSdkVersion: version.raw,
            deleteAll: true,
        })
    }

    /**
     * Get the organizations the user belongs to.
     * @returns {Promise} A list of organizations the user belongs to
     */
    public async getOrganizations() {
        const response = await this._apiClient.getMe()

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return response.result.organizations!!
    }

    /**
     * Fetch all the handles associated with this user from the SlashID API.
     * @returns {Promise} A list of handles available for the user
     */
    public async getHandles() {
        const { errors, result } = await this._apiClient.getMe()

        if (errors) {
            throw errors
        }

        return result.handles
    }

    /**
     * Given an organization ID, get a new token for the same user in the Organization specified by the Organization ID.
     *
     * For the call to succeed the following conditions must be met:
     * - the token must be valid at the time of the request
     * - the two Organizations must share the same person pool
     * - the user must be a member of both Organizations
     *
     * This operation does not count as an authentication, so the new token will have the same expiration time as the original.
     * @param oid
     * @returns {Promise} A new token for the specified Organization ID
     */
    public async getTokenForOrganization(oid: string): Promise<domain.UserTokenText | domain.TokenContainerText> {
        const response = await this._apiClient.getToken({
            slashIDOrgID: oid,
            slashIDSdkVersion: version.raw,
        })

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return response.result!!
    }

    /**
     * User objects stringify to their token value for convenience:
     *
     * @example
     * ```js
     * user.toString() === "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJuYW1lIjoiSm9obiBTbWl0aCIsImlhdCI6MTUxNjIzOTAyMn0.dzKuKf6u9G7Crk9tsFnS2cey1zglWTFQv_hjWjmtXms"
     * ```
     */
    public toString() {
        return this._tokenContainer ?? this._token ?? ""
    }

    /**
     * User objects encode to JSON as a string containing their token value.
     *
     * @example
     * ```js
     * JSON.stringify(user) === "\"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJuYW1lIjoiSm9obiBTbWl0aCIsImlhdCI6MTUxNjIzOTAyMn0.dzKuKf6u9G7Crk9tsFnS2cey1zglWTFQv_hjWjmtXms\""
     * ```
     */
    public toJSON() {
        return `${this._tokenContainer ?? this._token ?? ""}`
    }
}
